"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import type { ApiDropContextProfileContext } from "@/generated/models/ApiDropContextProfileContext";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { commonApiDelete, commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext, useRef } from "react";
export interface DropCurationTarget {
  readonly dropId: string;
  readonly waveId: string;
  readonly isCuratable: boolean;
  readonly isCurated: boolean;
}
interface CurationMutationParams {
  readonly target: DropCurationTarget;
  readonly action: "curate" | "uncurate";
}
interface UseDropCurationMutationReturn {
  readonly addCuration: (target: DropCurationTarget) => void;
  readonly removeCuration: (target: DropCurationTarget) => void;
  readonly toggleCuration: (target: DropCurationTarget) => void;
  readonly isPending: boolean;
}
const FALLBACK_DROP_CONTEXT: ApiDropContextProfileContext = {
  rating: 0,
  min_rating: 0,
  max_rating: 0,
  reaction: null,
  boosted: false,
  bookmarked: false,
  curatable: false,
  curated: false
};
export const useDropCurationMutation = () => {
  const $ = _c(30);
  const {
    connectedProfile,
    setToast
  } = useAuth();
  const {
    invalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const myStreamContext = useMyStreamOptional();
  const rollbackRef = useRef(null);
  const pendingDropIdRef = useRef(null);
  let t0;
  if ($[0] !== myStreamContext) {
    t0 = (target, curated) => {
      if (!myStreamContext?.applyOptimisticDropUpdate) {
        return null;
      }
      const handle = myStreamContext.applyOptimisticDropUpdate({
        waveId: target.waveId,
        dropId: target.dropId,
        update: draft => {
          if (draft.type !== DropSize.FULL) {
            return draft;
          }
          const baseContext = draft.context_profile_context ?? {
            ...FALLBACK_DROP_CONTEXT,
            curatable: target.isCuratable,
            curated: target.isCurated
          };
          draft.context_profile_context = {
            ...baseContext,
            curated
          };
          return draft;
        }
      });
      return handle?.rollback ?? null;
    };
    $[0] = myStreamContext;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const applyOptimisticCuration = t0;
  let t1;
  if ($[2] !== applyOptimisticCuration) {
    t1 = t2 => {
      const {
        target: target_1,
        action: action_0
      } = t2;
      rollbackRef.current?.();
      rollbackRef.current = applyOptimisticCuration(target_1, action_0 === "curate");
      pendingDropIdRef.current = target_1.dropId;
    };
    $[2] = applyOptimisticCuration;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== invalidateDrops) {
    t2 = () => {
      rollbackRef.current = null;
      pendingDropIdRef.current = null;
      invalidateDrops();
    };
    $[4] = invalidateDrops;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== setToast) {
    t3 = (error, t4) => {
      const {
        action: action_1
      } = t4;
      rollbackRef.current?.();
      rollbackRef.current = null;
      pendingDropIdRef.current = null;
      const errorMessage = error instanceof Error ? error.message : String(error);
      setToast({
        message: action_1 === "curate" ? `Failed to curate drop: ${errorMessage}` : `Failed to remove curation: ${errorMessage}`,
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: _temp,
      onMutate: t1,
      onSuccess: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const mutation = useMutation(t4);
  let t5;
  if ($[12] !== connectedProfile || $[13] !== mutation.isPending || $[14] !== setToast) {
    t5 = target_2 => {
      if (!connectedProfile) {
        setToast({
          message: "Please connect your wallet to curate drops",
          type: "warning"
        });
        return false;
      }
      if (!target_2.isCuratable) {
        return false;
      }
      if (target_2.dropId.startsWith("temp-")) {
        return false;
      }
      if (pendingDropIdRef.current === target_2.dropId || mutation.isPending) {
        return false;
      }
      return true;
    };
    $[12] = connectedProfile;
    $[13] = mutation.isPending;
    $[14] = setToast;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const canMutate = t5;
  let t6;
  if ($[16] !== canMutate || $[17] !== mutation) {
    t6 = target_3 => {
      if (!canMutate(target_3)) {
        return;
      }
      mutation.mutate({
        target: target_3,
        action: "curate"
      });
    };
    $[16] = canMutate;
    $[17] = mutation;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const addCuration = t6;
  let t7;
  if ($[19] !== canMutate || $[20] !== mutation) {
    t7 = target_4 => {
      if (!canMutate(target_4)) {
        return;
      }
      mutation.mutate({
        target: target_4,
        action: "uncurate"
      });
    };
    $[19] = canMutate;
    $[20] = mutation;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const removeCuration = t7;
  let t8;
  if ($[22] !== addCuration || $[23] !== removeCuration) {
    t8 = target_5 => {
      if (target_5.isCurated) {
        removeCuration(target_5);
      } else {
        addCuration(target_5);
      }
    };
    $[22] = addCuration;
    $[23] = removeCuration;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const toggleCuration = t8;
  let t9;
  if ($[25] !== addCuration || $[26] !== mutation.isPending || $[27] !== removeCuration || $[28] !== toggleCuration) {
    t9 = {
      addCuration,
      removeCuration,
      toggleCuration,
      isPending: mutation.isPending
    };
    $[25] = addCuration;
    $[26] = mutation.isPending;
    $[27] = removeCuration;
    $[28] = toggleCuration;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
async function _temp(t0) {
  const {
    target: target_0,
    action
  } = t0;
  const endpoint = `drops/${target_0.dropId}/curations`;
  if (action === "curate") {
    await commonApiPostWithoutBodyAndResponse({
      endpoint
    });
  } else {
    await commonApiDelete({
      endpoint
    });
  }
  return {
    target: target_0,
    action
  };
}